import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { isElement } from 'react-is';

import type { DataTableColumnDef, DataTableRowData } from '../../public.api.js';

/**
 * Props for the `DataTableCellActions` component.
 * @public
 */
export interface DataTableCellActionsProps<TData extends DataTableRowData> {
  /**
   * Content for the cell actions.
   * Should be a `TableActionsMenu` directly or a function returning one.
   */
  children: DataTableCellActionsContent<TData>;
  /**
   * Column ID that the given cell actions apply to. When a function is provided, it receives a
   * column ID and must return a boolean whether the action should be applied to that column.
   * If left empty, the actions apply to all cells in columns that allow cell actions.
   */
  column?: string | ((columnId: string) => boolean);
}

/**
 * Type for the content of a DataTableCellAction.
 * @public
 */
export type DataTableCellActionsContent<TData extends DataTableRowData> =
  | ReactNode
  | ((props: {
      columnDef: DataTableColumnDef<TData>;
      cellValue: unknown;
      row: TData;
      rowId: string;
    }) => ReactElement<PropsWithChildren>);

/**
 * Type for the cell actions collected from the table configuration.
 * `column` is the extracted column prop of the CellActions slot component
 * and `action` is the content.
 * @internal
 */
export type CollectedCellActions<TData extends DataTableRowData> = Array<{
  column?: string | ((id: string) => boolean);
  action: DataTableCellActionsContent<TData>;
}>;

/**
 * The slot component for defining cell actions within the DataTable.
 * @public
 */
export function DataTableCellActions<TData extends DataTableRowData>(
  props: DataTableCellActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is of type DataTableCellActions.
 * @internal
 */
export function isDataTableCellActions<TData extends DataTableRowData>(
  child: ReactNode,
): child is ReactElement<DataTableCellActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableCellActions) {
    return true;
  }
  return false;
}
